// Shared enums. API §1.2 (entities) · Content §4.4 (notification registry) · Permissions §1.
import { z } from 'zod';

export const membershipRole = z.enum(['member', 'admin']);
export type MembershipRole = z.infer<typeof membershipRole>;

export const membershipStatus = z.enum(['active', 'holding']);
export type MembershipStatus = z.infer<typeof membershipStatus>;

export const textSizePref = z.enum(['default', 'large']);
export type TextSizePref = z.infer<typeof textSizePref>;

export const cycleStatus = z.enum([
  'awaiting_theme',
  'open',
  'closed',
  'compiling',
  'dropped',
  'failed',
]);
export type CycleStatus = z.infer<typeof cycleStatus>;

export const themeSource = z.enum(['editor', 'auto-fallback']);

export const promptType = z.enum(['theme', 'adhoc']);

export const contributionStatus = z.enum(['draft', 'submitted']);
export type ContributionStatus = z.infer<typeof contributionStatus>;

export const editionStatus = z.enum(['compiling', 'ready', 'partial', 'failed']);
export type EditionStatus = z.infer<typeof editionStatus>;

export const departmentType = z.enum([
  'puzzle',
  'horoscope',
  'food',
  'travel',
  'photo_collection',
  'occasions',
]);
export type DepartmentType = z.infer<typeof departmentType>;

/** Fixed reader order, API §3.6 step 2. */
export const DEPARTMENT_ORDER: DepartmentType[] = [
  'occasions',
  'puzzle',
  'horoscope',
  'food',
  'travel',
  'photo_collection',
];

export const departmentStatus = z.enum(['pending', 'generating', 'ready', 'failed', 'skipped']);
export type DepartmentStatus = z.infer<typeof departmentStatus>;

export const occasionSubjectType = z.enum(['member', 'pet']);
export const occasionType = z.enum(['birthday', 'anniversary', 'custom']);
export const occasionRecurrence = z.enum(['annual', 'one_time']);

export const reactionTargetType = z.enum(['page', 'department']);
export const reactionType = z.enum(['heart']);

export const devicePlatform = z.enum(['web', 'android', 'ios']);

export const photoMimeType = z.enum(['image/jpeg', 'image/png', 'image/webp']);

/** Notification Type Registry — App Flow §2, Content §4.4. All 13 types. */
export const notificationType = z.enum([
  'prompt_reminder',
  'theme_picked',
  'drop_day',
  'heart_received',
  'editor_rotation',
  'occasion_reminder',
  'new_member_joined',
  'archive_unlocked',
  'member_removed',
  'admin_transferred',
  'auto_fallback_theme',
  'compilation_complete',
  'bonus_prompt',
]);
export type NotificationType = z.infer<typeof notificationType>;

/** Preference toggles are ignored for these (App Flow §6, API §1.2 NotificationPreference). */
export const SYSTEM_CRITICAL_NOTIFICATIONS: NotificationType[] = [
  'admin_transferred',
  'member_removed',
];

/** Cron job names — API §5. */
export const cronJobName = z.enum([
  'cutoff_enforcer',
  'compilation_trigger',
  'drop_scheduler',
  'prompt_reminder',
  'occasion_reminder',
  'editor_timeout',
  'pii_purge',
]);
export type CronJobName = z.infer<typeof cronJobName>;

/** AI pipeline stages — API §3, AiCall.stage. */
export const aiStage = z.enum(['C1', 'C2', 'C3', 'C4.1', 'C4.2', 'C4.3', 'C4.4', 'C4.5', 'C4.6']);
export type AiStage = z.infer<typeof aiStage>;

export const aiCallStatus = z.enum(['ok', 'error', 'fallback']);

/** Limits that both client and server enforce. API §2.7–2.8, §2.6. */
export const LIMITS = {
  photosPerContribution: 5,
  photoMaxBytes: 10 * 1024 * 1024,
  adhocPromptsPerCycle: 2,
  promptMaxChars: 200,
  themeMaxChars: 60,
  contributionMaxChars: 5000,
  photosPerCollection: 30,
  inviteTokenDays: 7,
  editorTimeoutDays: 7,
  piiPurgeDays: 30,
} as const;
